import type { FC} from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import {
  Collapse,
  ExpandLessIcon as ExpandLess,
  ExpandMoreIcon as ExpandMore,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
} from '@/shared/ui/mui';
import type { MenuItemProps } from '../types/index';

const MenuItem: FC<MenuItemProps> = ({
  icon: IconComponent,
  text,
  labelKey,
  path,
  subItems,
  onClose,
}) => {
  const location = useLocation();
  const theme = useTheme();
  const { t } = useTranslation('navigation');
  // Translate when a labelKey is present; fall back to the English `text`.
  const label = labelKey ? t(labelKey, { defaultValue: text }) : text;

  const isActive = path ? location.pathname === path || location.pathname.startsWith(`${path}/`) : false;
  const hasActiveChild = subItems?.some(sub => sub.path && (location.pathname === sub.path || location.pathname.startsWith(`${sub.path}/`))) ?? false;

  const [open, setOpen] = useState(hasActiveChild);

  useEffect(() => {
    if (hasActiveChild) setOpen(true);
  }, [hasActiveChild]);

  const handleClick = () => {
    if (subItems && subItems.length > 0) {
      setOpen(!open);
    } else if (path) {
      onClose();
    }
  };

  return (
    <>
      <ListItem disablePadding>
        <ListItemButton
          component={subItems ? 'div' : Link}
          to={subItems ? undefined : path || '#'}
          onClick={handleClick}
          sx={{
            mx: 1,
            borderRadius: 1,
            py: 1.5,
            ...(isActive || hasActiveChild) && {
              bgcolor: theme.palette.primary.ultraLight,
              '& .MuiListItemIcon-root': {
                color: theme.palette.primary.main,
              },
              '& .MuiListItemText-primary': {
                color: theme.palette.primary.main,
                fontWeight: 600,
              },
            },
            '&:hover': {
              bgcolor: theme.palette.primary.light,
              '& .MuiListItemIcon-root': {
                color: theme.palette.primary.main,
              },
              '& .MuiListItemText-primary': {
                color: theme.palette.primary.main,
              },
            },
          }}
        >
          {IconComponent && (
            <ListItemIcon>
              <IconComponent />
            </ListItemIcon>
          )}
          <ListItemText primary={label} />
          {subItems && (open ? <ExpandLess /> : <ExpandMore />)}
        </ListItemButton>
      </ListItem>

      {subItems && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {subItems.map((subItem) => {
              const isSubActive = subItem.path ? location.pathname === subItem.path || location.pathname.startsWith(`${subItem.path}/`) : false;
              return (
              <ListItem key={subItem.text} disablePadding>
                <ListItemButton
                  component={Link}
                  to={subItem.path || '#'}
                  onClick={onClose}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    pl: 4,
                    py: 1.5,
                    ...isSubActive && {
                      bgcolor: theme.palette.primary.ultraLight,
                      '& .MuiListItemIcon-root': {
                        color: theme.palette.primary.main,
                      },
                      '& .MuiListItemText-primary': {
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                      },
                    },
                    '&:hover': {
                      bgcolor: theme.palette.primary.light,
                      '& .MuiListItemIcon-root': {
                        color: theme.palette.primary.main,
                      },
                      '& .MuiListItemText-primary': {
                        color: theme.palette.primary.main,
                      },
                    },
                  }}
                >
                  {subItem.icon && (
                    <ListItemIcon>
                      <subItem.icon />
                    </ListItemIcon>
                  )}
                  <ListItemText
                    primary={
                      subItem.labelKey
                        ? t(subItem.labelKey, { defaultValue: subItem.text })
                        : subItem.text
                    }
                  />
                </ListItemButton>
              </ListItem>
              );
            })}
          </List>
        </Collapse>
      )}
    </>
  );
};

export default MenuItem;
